'use client';
import Link from 'next/link';
import { Avatar, Box, Card, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { EarningEvent } from '@/types/earnings.types';
import { getDotColor } from '@/utils/earnings.utils';
import { formatPercentage } from '@/utils/formatters';

import { ROUTES } from '@/constants';

interface EarningCardProps {
  event: EarningEvent;
  variant?: 'mobile' | 'desktop';
}

export const EarningCard = ({ event, variant = 'desktop' }: EarningCardProps) => {
  const { t } = useT();
  const isMobile = variant === 'mobile';

  const percentageStr = formatPercentage(event?.odds || 0, 0);

  return (
    <Link href={ROUTES.MARKET_DETAILS(event?.id)}>
      <Card
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          borderRadius: '8px',
          border: isMobile ? '1px solid' : 'none',
          borderColor: isMobile ? 'divider' : 'transparent',
          bgcolor: isMobile ? 'background.paper' : 'action.hover',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.selected',
          },
        }}
      >
        {/* Company Logo */}
        <Avatar
          src={event?.companyLogo}
          alt={event?.ticker || ''}
          variant="rounded"
          sx={{
            width: isMobile ? 40 : 36,
            height: isMobile ? 40 : 36,
            borderRadius: '8px',
            flexShrink: 0,
            '& img': {
              objectFit: 'contain',
              p: 0.5,
            },
          }}
        >
          {event?.ticker || ''}
        </Avatar>

        {/* Event Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: 'text.primary',
              mb: 0.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={event?.companyName}
          >
            {event?.ticker || ''}
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? '0.875rem' : '0.75rem',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            {event?.epsTarget} EPS
          </Typography>
        </Box>

        {/* Odds/Percentage */}
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              justifyContent: 'flex-end',
              mb: isMobile ? 0.25 : 0,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: getDotColor(percentageStr),
              }}
            />
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {percentageStr}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {t('earnings.beats')}
          </Typography>
        </Box>
      </Card>
    </Link>
  );
};
